import React, { useState } from 'react';

const SECFilingList = ({
  filings,
  loading,
  onAnalyzeFilings
}) => {
  const [sortField, setSortField] = useState('retrieved_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [companyFilter, setCompanyFilter] = useState('');
  const [formTypeFilter, setFormTypeFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedFilings, setSelectedFilings] = useState([]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getFilteredAndSortedFilings = () => {
    // Apply filters
    let filtered = filings.filter(filing => {
      // Company name filter
      if (companyFilter && !filing.company_name?.toLowerCase().includes(companyFilter.toLowerCase())) {
        return false;
      }

      // Form type filter
      if (formTypeFilter && filing.form_type !== formTypeFilter) {
        return false;
      }

      // Date range filter
      if (startDate || endDate) {
        const filingDate = new Date(filing.filing_date);
        if (startDate && filingDate < new Date(startDate)) {
          return false;
        }
        if (endDate && filingDate > new Date(endDate)) {
          return false;
        }
      }

      return true;
    });

    // Sort
    filtered.sort((a, b) => {
      let aVal, bVal;

      switch (sortField) {
        case 'company_name':
          aVal = (a.company_name || '').toLowerCase();
          bVal = (b.company_name || '').toLowerCase();
          break;
        case 'form_type':
          aVal = a.form_type || '';
          bVal = b.form_type || '';
          break;
        case 'filing_date':
          aVal = new Date(a.filing_date || 0);
          bVal = new Date(b.filing_date || 0);
          break;
        case 'retrieved_at':
          aVal = new Date(a.retrieved_at || 0);
          bVal = new Date(b.retrieved_at || 0);
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  };

  // Get unique form types for dropdown
  const uniqueFormTypes = [...new Set(filings.map(f => f.form_type))].sort();

  const getSortIcon = (field) => {
    if (sortField !== field) return <i className="bi bi-arrow-down-up text-muted"></i>;
    return sortDirection === 'asc' ? 
      <i className="bi bi-arrow-up"></i> : 
      <i className="bi bi-arrow-down"></i>;
  };

  const handleSelectFiling = (filing) => {
    setSelectedFilings(prev => {
      const isSelected = prev.some(f => f.filing_id === filing.filing_id);
      if (isSelected) {
        return prev.filter(f => f.filing_id !== filing.filing_id);
      } else {
        if (prev.length >= 5) {
          alert('You can only select up to 5 filings at a time');
          return prev;
        }
        return [...prev, filing];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedFilings.length === filteredFilings.length) {
      setSelectedFilings([]);
    } else {
      setSelectedFilings(filteredFilings.slice(0, 5));
    }
  };

  const handleAnalyzeSelected = () => {
    if (selectedFilings.length === 0) {
      alert('Please select at least one filing to analyze');
      return;
    }
    onAnalyzeFilings(selectedFilings);
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading filings...</p>
      </div>
    );
  }

  const filteredFilings = getFilteredAndSortedFilings();

  return (
    <div>
      {/* Filters Section */}
      <div className="card mb-3">
        <div className="card-body">
          <h6 className="card-title mb-3">Filters</h6>
          <div className="row g-3">
            {/* Company Name Filter */}
            <div className="col-md-3">
              <label htmlFor="companyFilter" className="form-label">Company Name</label>
              <input
                type="text"
                id="companyFilter"
                className="form-control"
                placeholder="Search company..."
                value={companyFilter}
                onChange={(e) => setCompanyFilter(e.target.value)}
              />
            </div>

            {/* Form Type Filter */}
            <div className="col-md-2">
              <label htmlFor="formTypeFilter" className="form-label">Form Type</label>
              <select
                id="formTypeFilter"
                className="form-select"
                value={formTypeFilter}
                onChange={(e) => setFormTypeFilter(e.target.value)}
              >
                <option value="">All Types</option>
                {uniqueFormTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            {/* Start Date Filter */}
            <div className="col-md-3">
              <label htmlFor="startDate" className="form-label">Filing Date From</label>
              <input
                type="date"
                id="startDate"
                className="form-control"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            {/* End Date Filter */}
            <div className="col-md-3">
              <label htmlFor="endDate" className="form-label">Filing Date To</label>
              <input
                type="date"
                id="endDate"
                className="form-control"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>

            {/* Clear Filters Button */}
            <div className="col-md-1 d-flex align-items-end">
              <button
                className="btn btn-outline-secondary w-100"
                onClick={() => {
                  setCompanyFilter('');
                  setFormTypeFilter('');
                  setStartDate('');
                  setEndDate('');
                }}
                title="Clear all filters"
              >
                <i className="bi bi-x-circle"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {filteredFilings.length === 0 ? (
        <div className="alert alert-info">
          <i className="bi bi-info-circle me-2"></i>
          {filings.length === 0 
            ? 'No filings found. Trigger a manual poll or wait for scheduled polls to retrieve filings.'
            : 'No filings match the current filters. Try adjusting your filter criteria.'}
        </div>
      ) : (
        <>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="text-muted">
              Showing {filteredFilings.length} of {filings.length} filing{filings.length !== 1 ? 's' : ''}
              {selectedFilings.length > 0 && (
                <span className="ms-2 text-primary">
                  ({selectedFilings.length} selected)
                </span>
              )}
            </div>
            {selectedFilings.length > 0 && (
              <button
                className="btn btn-primary"
                onClick={handleAnalyzeSelected}
              >
                <i className="bi bi-search me-2"></i>
                Analyze Selected ({selectedFilings.length})
              </button>
            )}
          </div>

          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={selectedFilings.length === filteredFilings.length && filteredFilings.length > 0}
                      onChange={handleSelectAll}
                      title="Select/Deselect all (max 5)"
                    />
                  </th>
                  <th 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('company_name')}
                  >
                    Company {getSortIcon('company_name')}
                  </th>
                  <th>CIK</th>
                  <th 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('form_type')}
                  >
                    Form Type {getSortIcon('form_type')}
                  </th>
                  <th 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('filing_date')}
                  >
                    Filing Date {getSortIcon('filing_date')}
                  </th>
                  <th 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('retrieved_at')}
                  >
                    Retrieved {getSortIcon('retrieved_at')}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredFilings.map(filing => (
                  <tr 
                    key={filing.filing_id}
                    className={selectedFilings.some(f => f.filing_id === filing.filing_id) ? 'table-active' : ''}
                  >
                    <td>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedFilings.some(f => f.filing_id === filing.filing_id)}
                        onChange={() => handleSelectFiling(filing)}
                      />
                    </td>
                    <td>
                      <strong>{filing.company_name}</strong>
                      {filing.primary_doc_description && (
                        <div className="small text-muted">{filing.primary_doc_description}</div>
                      )}
                    </td>
                    <td>
                      <span className="badge bg-light text-dark">{filing.cik}</span>
                    </td>
                    <td>
                      <span className="badge bg-info text-dark">{filing.form_type}</span>
                    </td>
                    <td>{filing.filing_date}</td>
                    <td className="small text-muted">{formatDate(filing.retrieved_at)}</td>
                    <td>
                      {filing.filing_url && (
                        <a
                          href={filing.filing_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-primary me-2"
                          title="View on SEC EDGAR"
                        >
                          <i className="bi bi-box-arrow-up-right"></i>
                        </a>
                      )}
                      {filing.s3_key && (
                        <button
                          className="btn btn-sm btn-outline-success"
                          title="Document stored in S3"
                        >
                          <i className="bi bi-cloud-check"></i>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default SECFilingList;
